import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Camera, Check, Upload, FileText, Plus, X, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ScanBulletinPage = () => {
  const { woId, orderId } = useParams();
  const [photos, setPhotos] = useState([]);
  const [bulletinNumber, setBulletinNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const label = photos.length === 0 ? 'Recto' : photos.length === 1 ? 'Verso' : `Page ${photos.length + 1}`;
      setPhotos(prev => [...prev, { file, preview: ev.target.result, label }]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (photos.length === 0) { toast.error('Prenez au moins une photo'); return; }
    if (!bulletinNumber.trim()) { toast.error('Numero de bulletin requis'); return; }

    setUploading(true);
    let success = 0;
    for (const p of photos) {
      const formData = new FormData();
      formData.append('file', p.file);
      formData.append('bulletin_number', bulletinNumber.trim());
      try {
        await axios.post(`${API}/atelier/work-orders/${woId}/bulletin-upload/${orderId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success++;
      } catch (err) {
        toast.error(`Erreur photo ${p.label}: ${err.response?.data?.detail || 'Erreur'}`);
      }
    }
    setUploading(false);

    if (success > 0) {
      toast.success(`${success} photo${success > 1 ? 's' : ''} envoyee${success > 1 ? 's' : ''} !`);
      setUploadedCount(prev => prev + success);
      setPhotos([]);
      setBulletinNumber('');
    }
  };

  const resetForNew = () => {
    setPhotos([]);
    setBulletinNumber('');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Toaster position="top-center" />
      <Card className="p-5 max-w-sm w-full space-y-4">
        <div className="text-center">
          <FileText className="w-10 h-10 text-blue-600 mx-auto mb-2" />
          <h2 className="text-lg font-bold text-slate-900">Scan bulletin de livraison</h2>
          <p className="text-xs text-slate-500 mt-1">Fiche: {woId?.substring(0, 8)}</p>
          {uploadedCount > 0 && (
            <p className="text-xs text-emerald-600 font-medium mt-1" data-testid="uploaded-counter">
              {uploadedCount} photo{uploadedCount > 1 ? 's' : ''} deja envoyee{uploadedCount > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Photo previews */}
        {photos.length > 0 && (
          <div className="space-y-2" data-testid="photo-previews">
            {photos.map((p, i) => (
              <div key={i} className="relative rounded-lg border overflow-hidden" data-testid={`photo-preview-${i}`}>
                <img src={p.preview} alt={p.label} className="w-full h-32 object-cover" />
                <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                  {p.label}
                </span>
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  data-testid={`remove-photo-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add photo buttons */}
        <div className="space-y-2">
          <Button
            className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="open-camera-btn"
          >
            <Camera className="w-5 h-5 mr-2" />
            {photos.length === 0 ? 'Prendre une photo' : 'Ajouter verso / autre page'}
          </Button>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />

          <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} data-testid="upload-file-btn">
            <Upload className="w-4 h-4 mr-2" /> Choisir un fichier
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoCapture} />

          {photos.length > 0 && (
            <p className="text-center text-xs text-slate-400">
              {photos.length} photo{photos.length > 1 ? 's' : ''} prete{photos.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Bulletin number */}
        <div>
          <Label className="font-bold">N de bulletin *</Label>
          <Input
            value={bulletinNumber}
            onChange={(e) => setBulletinNumber(e.target.value)}
            placeholder="Ex: BL-2026-001"
            className="h-12 text-lg font-mono"
            data-testid="bulletin-number-input"
          />
        </div>

        {/* Submit */}
        <Button
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
          onClick={handleUpload}
          disabled={photos.length === 0 || !bulletinNumber.trim() || uploading}
          data-testid="submit-bulletin-btn"
        >
          {uploading ? 'Envoi en cours...' : (
            <><Check className="w-5 h-5 mr-2" /> Envoyer {photos.length > 1 ? `${photos.length} photos` : 'la photo'}</>
          )}
        </Button>

        {/* Scan another bulletin */}
        {uploadedCount > 0 && photos.length === 0 && (
          <div className="pt-2 border-t space-y-2">
            <Button
              variant="outline"
              className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={resetForNew}
              data-testid="scan-another-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Scanner un autre bulletin
            </Button>
            <p className="text-center text-xs text-slate-400">Ou fermez cette page si vous avez termine</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ScanBulletinPage;
